import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import TopBar from "../components/TopBar";
import EmptyState from "../components/EmptyState";
import { Modal, Field } from "./Fields";
import ReceiptUploader, { ReceiptBadge, ReceiptGallery } from "../components/ReceiptUploader";
import AddableSelect from "../components/AddableSelect";
import { api, fmtUSD, fmtNum, todayISO } from "../lib/api";
import { downloadCsv } from "../lib/csv";
import { useYear } from "../context/YearContext";
import { Plus, Wheat, Trash2, Pencil, Download } from "lucide-react";
import { toast } from "sonner";

const UNITS = ["bushels", "lbs", "tons", "cwt", "bales"];
const blank = { field_id: "", plot_id: "", date: todayISO(), crop: "", quantity: "", unit: "bushels", price_per_unit: "", notes: "", receipts: [], location_id: "", destination: "bin", contract_id: "", contract_sale_date: "", overrun_quantity: "", overrun_price_per_unit: "" };

export default function Yields() {
  const { year, refreshYears } = useYear();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [fields, setFields] = useState([]);
  const [locations, setLocations] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [galleryRow, setGalleryRow] = useState(null);

  // Mobile FAB entry point
  useEffect(() => {
    if (searchParams.get("quick") === "1") {
      setEditing(null);
      setForm({ ...blank, date: todayISO() });
      setOpen(true);
      searchParams.delete("quick");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const load = async () => {
    const [r, f, l, c] = await Promise.all([
      api.get(`/yields?year=${year}`),
      api.get(`/fields?year=${year}`),
      api.get(`/locations`),
      api.get(`/contracts?with_remaining=true`),
    ]);
    setRows(r.data);
    setFields(f.data);
    setLocations(l.data);
    setContracts(c.data);
  };

  useEffect(() => {
    load();
  }, [year]);

  const totalRevenue = rows.reduce((s, r) => s + (r.total_revenue || 0), 0);

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      quantity: parseFloat(form.quantity) || 0,
      price_per_unit: parseFloat(form.price_per_unit) || 0,
      overrun_quantity: form.destination === "contract" ? (parseFloat(form.overrun_quantity) || 0) : 0,
      overrun_price_per_unit: form.destination === "contract" ? (parseFloat(form.overrun_price_per_unit) || 0) : 0,
      contract_sale_date: form.destination === "contract" ? (form.contract_sale_date || "") : "",
      year,
    };
    try {
      let res;
      if (editing) {
        res = await api.put(`/yields/${editing.id}`, payload);
        toast.success(res?.data?._offline_draft ? "Saved offline — will sync when back online" : "Harvest updated");
      } else {
        res = await api.post("/yields", payload);
        toast.success(res?.data?._offline_draft ? "Saved offline — will sync when back online" : "Harvest logged");
      }
      setOpen(false);
      setEditing(null);
      setForm({ ...blank, date: todayISO() });
      refreshYears();
      load();
    } catch {
      toast.error("Could not save harvest");
    }
  };

  const onEdit = (r) => {
    // When editing a contract fill with overrun, surface the sibling's values
    // so the user can tweak them in-place.
    const sibling = r.destination === "contract" ? rows.find((x) => x.parent_yield_id === r.id) : null;
    setEditing(r);
    setForm({
      field_id: r.field_id,
      plot_id: r.plot_id || "",
      // If this is a contract fill with a saved harvest_date, prefer that as
      // the "harvest" date in the form, and surface the stored `date` as the
      // contract sale date.
      date: r.harvest_date || r.date,
      crop: r.crop,
      quantity: r.quantity,
      unit: r.unit,
      price_per_unit: r.price_per_unit,
      notes: r.notes || "",
      receipts: r.receipts || [],
      location_id: r.location_id || "",
      destination: r.destination || (r.location_id ? "bin" : "cash_sale"),
      contract_id: r.contract_id || "",
      contract_sale_date: r.destination === "contract" && r.harvest_date ? r.date : "",
      overrun_quantity: sibling ? sibling.quantity : "",
      overrun_price_per_unit: sibling ? sibling.price_per_unit : "",
    });
    setOpen(true);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this harvest record?")) return;
    await api.delete(`/yields/${id}`);
    toast.success("Harvest deleted");
    load();
  };

  return (
    <div>
      <TopBar
        title="Yield Log"
        subtitle={`Every harvest, every field, for ${year}. Revenue auto-calculates from quantity × price.`}
        actions={
          <div className="flex items-center gap-2 mt-6">
            <button
              data-testid="export-yields-csv"
              onClick={() => downloadCsv(`yields-${year}.csv`, rows.map((r) => ({
                date: r.date, field: r.field_name, crop: r.crop,
                quantity: r.quantity, unit: r.unit, price_per_unit: r.price_per_unit,
                total_revenue: r.total_revenue, notes: r.notes || "",
              })))}
              disabled={!rows.length}
              className="border border-[#1F1E1B] px-4 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#1F1E1B] hover:text-white transition-colors flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" /> CSV
            </button>
            <button
              data-testid="add-yield-btn"
              onClick={() => {
                if (!fields.length) {
                  toast.error("Add a field first.");
                  return;
                }
                setEditing(null);
                setForm({ ...blank, date: todayISO(), field_id: fields[0]?.id || "", crop: fields[0]?.crop_type || "" });
                setOpen(true);
              }}
              className="bg-[#BC4727] text-white px-5 py-2 font-bold uppercase tracking-wider text-sm hover:bg-[#9D391E] transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Log Harvest
            </button>
          </div>
        }
      />

      <div className="bg-white border border-[#1F1E1B] p-4 flex items-center justify-between mb-5" data-testid="yield-summary-bar">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">Total Gross Revenue</div>
          <div className="font-display text-3xl font-black tabular">{fmtUSD(totalRevenue)}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">Records</div>
          <div className="font-display text-3xl font-black tabular">{rows.length}</div>
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState
          icon={Wheat}
          title="No harvests logged"
          hint="Log a harvest after each load out of the field."
          testid="empty-yields"
        />
      ) : (
        <div className="bg-white border border-[#1F1E1B] overflow-x-auto">
          <table className="ledger-table" data-testid="yields-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Field</th>
                <th>Plot / Crop</th>
                <th className="num">Quantity</th>
                <th className="num">Price</th>
                <th className="num">Revenue</th>
                <th>Receipts</th>
                <th style={{ width: 90 }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const fld = fields.find((f) => f.id === r.field_id);
                const plot = fld?.plots?.find((p) => p.id === r.plot_id);
                const plotLabel = plot ? `${plot.crop} · ${fmtNum(plot.acres, 1)} ac` : r.crop;
                const isOverrun = !!r.parent_yield_id;
                return (
                <tr key={r.id} data-testid={`yield-row-${r.id}`} className={isOverrun ? "bg-[#FBE9E2]/40" : ""}>
                  <td className="tabular">
                    {isOverrun ? (
                      <span className="text-[10px] uppercase tracking-wider text-[#BC4727] font-bold mr-1">↳ Overrun</span>
                    ) : null}
                    {r.date}
                    {r.harvest_date && !isOverrun && (
                      <div className="text-[10px] text-[#66645E] font-normal">harvest {r.harvest_date}</div>
                    )}
                  </td>
                  <td className="font-semibold">{r.field_name || "—"}</td>
                  <td data-testid={`yield-plot-${r.id}`}>{plotLabel}</td>
                  <td className="num">{fmtNum(r.quantity)} {r.unit}</td>
                  <td className="num">{fmtUSD(r.price_per_unit)}</td>
                  <td className="num font-bold">{fmtUSD(r.total_revenue)}</td>
                  <td>
                    <ReceiptBadge receipts={r.receipts} onClick={() => setGalleryRow(r)} testid={`yield-receipts-${r.id}`} />
                  </td>
                  <td>
                    <div className="flex gap-1.5 justify-end">
                      {!isOverrun && (
                        <button data-testid={`edit-yield-${r.id}`} onClick={() => onEdit(r)} className="p-1.5 border border-[#1F1E1B] hover:bg-[#1F1E1B] hover:text-white transition-colors">
                          <Pencil className="w-3 h-3" />
                        </button>
                      )}
                      <button data-testid={`delete-yield-${r.id}`} onClick={() => onDelete(r.id)} className="p-1.5 border border-[#BC4727] text-[#BC4727] hover:bg-[#BC4727] hover:text-white transition-colors">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <Modal title={editing ? "Edit Harvest" : "Log Harvest"} onClose={() => setOpen(false)}>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="yield-form">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Date" required>
                <input data-testid="yield-date-input" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
              <Field label="Field" required>
                <select data-testid="yield-field-select" required value={form.field_id} onChange={(e) => {
                  const fld = fields.find(x => x.id === e.target.value);
                  const firstPlot = fld?.plots?.[0];
                  setForm({
                    ...form,
                    field_id: e.target.value,
                    plot_id: firstPlot?.id || "",
                    crop: firstPlot?.crop || fld?.crop_type || form.crop,
                  });
                }} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm">
                  <option value="">Select field</option>
                  {fields.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              </Field>
            </div>
            {(() => {
              const f = fields.find((x) => x.id === form.field_id);
              const plots = f?.plots || [];
              if (plots.length <= 1) return null;
              return (
                <Field label="Which crop on this field?" required>
                  <select data-testid="yield-plot-select" required value={form.plot_id} onChange={(e) => {
                    const p = plots.find((x) => x.id === e.target.value);
                    setForm({ ...form, plot_id: e.target.value, crop: p?.crop || form.crop });
                  }} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm">
                    <option value="">Select crop split…</option>
                    {plots.map((p) => (
                      <option key={p.id} value={p.id}>{p.crop} ({fmtNum(p.acres, 1)} ac)</option>
                    ))}
                  </select>
                </Field>
              );
            })()}
            <Field label="Crop" required>
              <input data-testid="yield-crop-input" required value={form.crop} onChange={(e) => setForm({ ...form, crop: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Quantity" required>
                <input data-testid="yield-quantity-input" type="number" step="0.01" required value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
              </Field>
              <Field label="Unit">
                <AddableSelect
                  domain="unit"
                  defaults={UNITS}
                  value={form.unit}
                  onChange={(v) => setForm({ ...form, unit: v })}
                  testid="yield-unit-select"
                />
              </Field>
              <Field label="Price / Unit" required>
                <input data-testid="yield-price-input" type="number" step="0.01" required value={form.price_per_unit} onChange={(e) => setForm({ ...form, price_per_unit: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
              </Field>
            </div>

            <div className="border border-[#1F1E1B] p-3">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E] mb-2">Where does this load go?</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
                {[
                  { v: "bin", label: "Bin / Storage" },
                  { v: "cash_sale", label: "Cash Sale" },
                  { v: "contract", label: "Fulfill Contract" },
                ].map((o) => (
                  <label key={o.v} className={`border px-3 py-2 text-sm font-semibold cursor-pointer text-center ${form.destination === o.v ? "bg-[#1F1E1B] text-white border-[#1F1E1B]" : "bg-white border-[#1F1E1B] hover:bg-[#F5F4F0]"}`}>
                    <input
                      type="radio"
                      className="hidden"
                      data-testid={`dest-${o.v}-radio`}
                      checked={form.destination === o.v}
                      onChange={() => {
                        const next = { ...form, destination: o.v };
                        if (o.v === "bin") { next.contract_id = ""; if (!next.price_per_unit) next.price_per_unit = "0"; }
                        if (o.v === "cash_sale") { next.location_id = ""; next.contract_id = ""; }
                        if (o.v === "contract") { next.location_id = ""; }
                        setForm(next);
                      }}
                    />
                    {o.label}
                  </label>
                ))}
              </div>
              {form.destination === "bin" && (
                <Field label="Stored at (bin or elevator)" required>
                  <select data-testid="yield-location-select" required value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm">
                    <option value="">Select…</option>
                    {locations.map((l) => <option key={l.id} value={l.id}>{l.name} ({l.type})</option>)}
                  </select>
                  {!locations.length && <div className="text-[11px] text-[#BC4727] mt-1">No locations yet — add a bin or elevator in Inventory first.</div>}
                </Field>
              )}
              {form.destination === "contract" && (
                <>
                  <Field label="Contract to fulfill" required>
                    <select
                      data-testid="yield-contract-select"
                      required
                      value={form.contract_id}
                      onChange={(e) => {
                        const c = contracts.find((x) => x.id === e.target.value);
                        setForm({
                          ...form,
                          contract_id: e.target.value,
                          price_per_unit: c ? c.price_per_unit : form.price_per_unit,
                          crop: c && !form.crop ? c.crop : form.crop,
                        });
                      }}
                      className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm"
                    >
                      <option value="">Select…</option>
                      {contracts.filter((c) => ["open", "partial"].includes(c.status)).map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.buyer} · {fmtNum(c.bushels, 0)} bu {c.crop} @ {fmtUSD(c.price_per_unit)}
                          {c.remaining !== undefined ? ` · ${fmtNum(c.remaining, 0)} bu left` : ""} ({c.status})
                        </option>
                      ))}
                    </select>
                    {contracts.filter((c) => ["open", "partial"].includes(c.status)).length === 0 && (
                      <div className="text-[11px] text-[#BC4727] mt-1">No open contracts. Create one in Inventory → Pre-Sold first.</div>
                    )}
                  </Field>
                  <Field label="Contract Sale Date (optional)">
                    <input
                      type="date"
                      data-testid="yield-contract-sale-date-input"
                      value={form.contract_sale_date}
                      onChange={(e) => setForm({ ...form, contract_sale_date: e.target.value })}
                      className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm"
                      placeholder="Leave blank if same as harvest"
                    />
                    <div className="text-[11px] text-[#66645E] mt-1">
                      Leave blank if the sale happened on the harvest date. Fill this when the elevator settled days or weeks later — revenue will land in the tax year of this date.
                    </div>
                  </Field>
                  {(() => {
                    const c = contracts.find((x) => x.id === form.contract_id);
                    if (!c || c.remaining === undefined) return null;
                    const qty = parseFloat(form.quantity) || 0;
                    const over = parseFloat(form.overrun_quantity) || 0;
                    const excess = qty - c.remaining;
                    if (excess <= 0.001) return null;
                    const covered = over >= excess - 0.001;
                    return (
                      <div
                        data-testid="overrun-alert"
                        className={`p-3 border-2 ${covered ? "border-[#1A4135] bg-[#E8F0EC]" : "border-[#BC4727] bg-[#FBE9E2] animate-pulse-slow"}`}
                      >
                        <div className="flex gap-2">
                          <span className="text-xl leading-none">{covered ? "✅" : "⚠️"}</span>
                          <div className="text-xs leading-relaxed">
                            <div className={`font-bold uppercase tracking-wider text-[11px] ${covered ? "text-[#1A4135]" : "text-[#BC4727]"} mb-1`}>
                              {covered ? "Overrun captured" : "Overrun detected"}
                            </div>
                            {covered ? (
                              <>You're logging <b>{fmtNum(over, 0)} bu</b> as overrun above the <b>{fmtNum(c.remaining, 0)} bu</b> remaining on this contract. Nice.</>
                            ) : (
                              <>
                                This fill is <b>{fmtNum(excess, 0)} bu</b> over what's left on {c.buyer}'s contract (contract has <b>{fmtNum(c.remaining, 0)} bu</b> remaining). Log the extra as overrun below so it hits a cash-sale row at your market price instead of the contract price.
                                {" "}
                                <button
                                  type="button"
                                  data-testid="overrun-autofill-btn"
                                  onClick={() => setForm((f) => ({
                                    ...f,
                                    quantity: c.remaining,
                                    overrun_quantity: excess.toFixed(2),
                                    overrun_price_per_unit: f.overrun_price_per_unit || f.price_per_unit,
                                  }))}
                                  className="underline font-bold hover:text-[#9D391E]"
                                >
                                  Auto-split for me →
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  <div className="border border-dashed border-[#BC4727] bg-[#FBE9E2]/30 p-3">
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C4A2B] mb-2">Overrun bushels (optional)</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Field label="Overrun qty">
                        <input
                          type="number"
                          step="0.01"
                          data-testid="yield-overrun-qty-input"
                          value={form.overrun_quantity}
                          onChange={(e) => setForm({ ...form, overrun_quantity: e.target.value })}
                          placeholder="0"
                          className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular"
                        />
                      </Field>
                      <Field label="Overrun price / unit">
                        <input
                          type="number"
                          step="0.01"
                          data-testid="yield-overrun-price-input"
                          value={form.overrun_price_per_unit}
                          onChange={(e) => setForm({ ...form, overrun_price_per_unit: e.target.value })}
                          placeholder="0.00"
                          className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular"
                        />
                      </Field>
                    </div>
                    <div className="text-[11px] text-[#5C4A2B] mt-2">
                      Bushels harvested beyond the contract — logged as a cash sale at this price. Shows up as a second row in the Yields table.
                    </div>
                  </div>
                </>
              )}
              <div className="text-[11px] text-[#66645E] bg-[#FAF7EC] border border-[#E5E2D6] p-2 mt-2">
                {form.destination === "bin" && (
                  <>Load increases the chosen bin/elevator. Revenue will be recorded when you sell from Inventory → Movements. You can set price to 0 for now.</>
                )}
                {form.destination === "cash_sale" && (
                  <>Full revenue is captured now. Inventory is not affected.</>
                )}
                {form.destination === "contract" && (
                  <>The contract balance decreases automatically. Price defaults to the contract price. Inventory is not affected.</>
                )}
              </div>
            </div>

            <Field label="Notes">
              <textarea data-testid="yield-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <ReceiptUploader
              receipts={form.receipts}
              onChange={(receipts) => setForm({ ...form, receipts })}
              testidPrefix="yield-receipt"
            />
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setOpen(false)} data-testid="yield-cancel-btn" className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
              <button type="submit" data-testid="yield-submit-btn" className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#133128]">Save Harvest</button>
            </div>
          </form>
        </Modal>
      )}

      {galleryRow && (
        <ReceiptGallery receipts={galleryRow.receipts} onClose={() => setGalleryRow(null)} />
      )}
    </div>
  );
}
